import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { formatReportDate } from '../utils/dateUtils';
import { pdf } from '@react-pdf/renderer';
import PilotRunReportPDF from '../components/PilotRunReportPDF';
import MigrosPilotRunReportPDF from '../components/MigrosPilotRunReportPDF';
import { useAllowedReportTemplates } from '../utils/reportTemplates';

type PdfTemplate = 'qa-mate' | 'migros';

const PilotRunReportDetail: React.FC = () => {
  const { id } = useParams();
  const { token, logout } = useAuth();
  const [inspection, setInspection] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [previewImage, setPreviewImage] = useState<string | null>(null);
  const [zoom, setZoom] = useState(1);
  const zoomContainerRef = useRef<HTMLDivElement>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [newComment, setNewComment] = useState('');
  const [selectedAttachment, setSelectedAttachment] = useState<{name: string, url: string} | null>(null);
  const [comments, setComments] = useState<any[]>([]);
  const [isPostingLog, setIsPostingLog] = useState(false);
  const [pendingAttachments, setPendingAttachments] = useState<{ url: string; name: string }[]>([]);
  const [isUploadingFile, setIsUploadingFile] = useState(false);

  // ── PDF generation state ──
  const [generatingPDF, setGeneratingPDF] = useState(false);
  const [downloadingImages, setDownloadingImages] = useState(false);
  const [pdfStatusIndex, setPdfStatusIndex] = useState(0);
  const [pdfModalOpen, setPdfModalOpen] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<PdfTemplate>('qa-mate');
  const [isTemplateDropdownOpen, setIsTemplateDropdownOpen] = useState(false);
  const templateDropdownRef = useRef<HTMLDivElement>(null);

  const templateOptions: { value: PdfTemplate; name: string; icon: string; iconBg: string; iconColor: string; description: string }[] = [
    {
      value: 'qa-mate',
      name: 'QA Mate Template',
      icon: 'verified',
      iconBg: 'bg-blue-100',
      iconColor: 'text-[#136bce]',
      description: 'Default QA Mate pilot run report layout.',
    },
    {
      value: 'migros',
      name: 'Migros Template',
      icon: 'inventory_2',
      iconBg: 'bg-orange-100',
      iconColor: 'text-orange-600',
      description: 'Migros Asia — Final Random Inspection (Textile/Hometextile) Version-1 layout, adapted to pilot run data.',
    },
  ];
  const allowedTemplates = useAllowedReportTemplates();
  const visibleTemplateOptions = templateOptions.filter(o => allowedTemplates.includes(o.value));
  const selectedTemplateOption =
    visibleTemplateOptions.find(t => t.value === selectedTemplate) ??
    visibleTemplateOptions[0] ??
    templateOptions[0];
  useEffect(() => {
    if (visibleTemplateOptions.length && !visibleTemplateOptions.some(o => o.value === selectedTemplate)) {
      setSelectedTemplate(visibleTemplateOptions[0].value);
    }
  }, [visibleTemplateOptions, selectedTemplate]);

  const pdfStatusMessages = [
    'Preparing pilot run data…',
    'Securing report metadata…',
    'Fetching pilot run images…',
    'Compiling evaluation findings…',
    'Embedding signatures…',
    'Rendering high-quality pages…',
    'Almost there — finalizing your PDF…',
  ];

  const [imageStatusIndex, setImageStatusIndex] = useState(0);
  const imageStatusMessages = [
    'Collecting image list…',
    'Fetching pilot run photos…',
    'Fetching CTQ detail images…',
    'Organizing into folders…',
    'Compressing into ZIP archive…',
    'Almost done — finalizing your download…',
  ];

  const baseUrl = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3000';

  const fetchLogs = useCallback(async (search?: string) => {
    if (!id || !token) return;
    try {
      const query = search ? `?search=${encodeURIComponent(search)}` : '';
      const res = await fetch(`${baseUrl}/api/v1/pilot-inspections/${id}/source-of-truth${query}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.status === 401) { logout(); return; }
      const data = await res.json();
      if (res.ok) {
        setComments((data.logs || []).map((log: any) => {
          let attachments: { name: string; url: string }[] = [];
          try {
            const urls = JSON.parse(log.attachment_url);
            const names = JSON.parse(log.attachment_name);
            if (Array.isArray(urls)) {
              attachments = urls.map((u: string, i: number) => ({ url: u, name: names[i] || 'Attachment' }));
            }
          } catch {
            if (log.attachment_url) {
              attachments = [{ url: log.attachment_url, name: log.attachment_name || 'Attachment' }];
            }
          }
          return {
            id: log.id,
            text: log.text,
            date: formatReportDate(log.created_at),
            author: log.author_name,
            attachments,
          };
        }));
      }
    } catch (err) {
      console.error('Failed to fetch source of truth logs:', err);
    }
  }, [id, token, baseUrl, logout]);

  useEffect(() => {
    if (inspection) fetchLogs();
  }, [inspection, fetchLogs]);

  useEffect(() => {
    if (!inspection) return;
    const timeout = setTimeout(() => fetchLogs(searchTerm || undefined), 300);
    return () => clearTimeout(timeout);
  }, [searchTerm, inspection, fetchLogs]);

  const filteredComments = comments;

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0 || !id || !token) return;
    setIsUploadingFile(true);
    try {
      for (const file of Array.from(files)) {
        const formData = new FormData();
        formData.append('files', file);
        const res = await fetch(`${baseUrl}/api/v1/pilot-inspections/${id}/source-of-truth/upload`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
          body: formData,
        });
        if (res.ok) {
          const data = await res.json();
          setPendingAttachments(prev => [...prev, { url: data.attachment_url, name: data.attachment_name }]);
        }
      }
    } catch (err) {
      console.error('Failed to upload attachment:', err);
    } finally {
      setIsUploadingFile(false);
      e.target.value = '';
    }
  };

  const handleAddComment = async () => {
    if (!newComment.trim() || !id || !token) return;
    setIsPostingLog(true);
    try {
      const res = await fetch(`${baseUrl}/api/v1/pilot-inspections/${id}/source-of-truth`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          text: newComment.trim(),
          attachment_urls: pendingAttachments.map(a => a.url),
          attachment_names: pendingAttachments.map(a => a.name),
        }),
      });
      if (res.status === 401) { logout(); return; }
      if (res.ok) {
        setNewComment('');
        setPendingAttachments([]);
        fetchLogs();
      }
    } catch (err) {
      console.error('Failed to post log:', err);
    } finally {
      setIsPostingLog(false);
    }
  };

  const openPreview = useCallback((url: string | null) => {
    setZoom(1);
    setPreviewImage(url);
  }, []);

  useEffect(() => {
    if (!generatingPDF) {
      setPdfStatusIndex(0);
      return;
    }
    const interval = setInterval(() => {
      setPdfStatusIndex((i) => (i + 1) % pdfStatusMessages.length);
    }, 3000);
    return () => clearInterval(interval);
  }, [generatingPDF]);

  useEffect(() => {
    if (!downloadingImages) {
      setImageStatusIndex(0);
      return;
    }
    const interval = setInterval(() => {
      setImageStatusIndex((i) => (i + 1) % imageStatusMessages.length);
    }, 2500);
    return () => clearInterval(interval);
  }, [downloadingImages]);

  useEffect(() => {
    if (!isTemplateDropdownOpen) return;
    const onClick = (e: MouseEvent) => {
      if (templateDropdownRef.current && !templateDropdownRef.current.contains(e.target as Node)) {
        setIsTemplateDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [isTemplateDropdownOpen]);

  useEffect(() => {
    const el = zoomContainerRef.current;
    if (!el || !previewImage) return;
    const handler = (e: WheelEvent) => {
      e.preventDefault();
      setZoom((prev: number) => Math.min(Math.max(0.25, prev - e.deltaY * 0.001), 5));
    };
    el.addEventListener('wheel', handler, { passive: false });
    return () => el.removeEventListener('wheel', handler);
  }, [previewImage]);

  useEffect(() => {
    const fetchInspection = async () => {
      try {
        setIsLoading(true);
        const response = await fetch(`${baseUrl}/api/v1/pilot-inspections/${id}`, {
          headers: {
            'Content-Type': 'application/json',
            ...(token && { Authorization: `Bearer ${token}` }),
          },
        });

        if (response.status === 401) {
          logout();
          return;
        }

        const result = await response.json();
        if (!response.ok) throw new Error(result.message || 'Failed to fetch inspection details');

        setInspection(result.data);
      } catch (err: any) {
        setError(err.message);
      } finally {
        setIsLoading(false);
      }
    };

    if (id && token) fetchInspection();
  }, [id, token, logout]);

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px] text-slate-500">
        <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-slate-200 border-t-blue-600 mb-4"></div>
        <p className="font-medium">Loading report details...</p>
      </div>
    );
  }

  if (error || !inspection) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px] text-red-500 p-8 text-center">
        <span className="material-symbols-outlined text-6xl mb-4">error</span>
        <h2 className="text-xl font-bold mb-2">Error Loading Report</h2>
        <p className="max-w-md mb-6">{error || 'Inspection not found'}</p>
        <Link to="/pilot-run-reports" className="px-6 py-2 bg-slate-100 text-slate-700 rounded-lg hover:bg-slate-200 transition-colors font-bold">
          Back to Reports
        </Link>
      </div>
    );
  }

  const handleDownload = () => {
    window.print();
  };

  // Convert any image URL to a base64 data URL so @react-pdf/renderer can embed
  // it without hitting CORS or auth barriers. GCS URLs are routed through the
  // same-origin API proxy; relative URLs are resolved against the API base.
  const toDataUrl = async (url: string | null | undefined): Promise<string> => {
    if (!url) return '';
    if (url.startsWith('data:')) return url;

    const blobToDataUrl = (blob: Blob): Promise<string> =>
      new Promise((res, rej) => {
        const reader = new FileReader();
        reader.onload = () => res(reader.result as string);
        reader.onerror = rej;
        reader.readAsDataURL(blob);
      });

    // Resolve relative paths against the API base
    let abs = url;
    if (!/^(https?:|blob:)/.test(abs)) abs = `${baseUrl}${abs.startsWith('/') ? '' : '/'}${abs}`;

    const isGcs = abs.includes('storage.googleapis.com');
    const fetchUrl = isGcs
      ? `${baseUrl}/api/v1/proxy-image?url=${encodeURIComponent(abs)}`
      : abs;

    try {
      const res = await fetch(fetchUrl, { mode: 'cors' });
      if (res.ok) return await blobToDataUrl(await res.blob());
      console.warn('[PDF img] fetch failed', res.status, fetchUrl);
    } catch (e) {
      console.warn('[PDF img] fetch threw', e, fetchUrl);
    }
    console.error('[PDF img] could NOT convert, image will likely be missing:', url);
    return abs;
  };

  const handleGeneratePDF = async (template: PdfTemplate = 'qa-mate') => {
    if (!inspection) return;
    setGeneratingPDF(true);
    try {
      // Deep-clone so we never mutate the displayed state
      const clone: any = JSON.parse(JSON.stringify(inspection));

      // URL dedup cache
      const cache = new Map<string, string>();
      const resolve = async (url: string | null | undefined): Promise<string> => {
        if (!url) return '';
        if (cache.has(url)) return cache.get(url)!;
        const data = await toDataUrl(url);
        cache.set(url, data);
        return data;
      };

      // Pilot run images
      for (const p of clone.photos || []) {
        if (p?.image_url) p.image_url = await resolve(p.image_url);
      }
      // CTQ evaluation images
      for (const c of clone.ctqImages || []) {
        if (c?.image_url) c.image_url = await resolve(c.image_url);
      }
      // Signatures
      if (clone.factory_signature) clone.factory_signature = await resolve(clone.factory_signature);
      if (clone.inspector_signature) clone.inspector_signature = await resolve(clone.inspector_signature);

      const docElement = template === 'migros'
        ? <MigrosPilotRunReportPDF inspection={clone} />
        : <PilotRunReportPDF inspection={clone} />;
      const filenamePrefix = template === 'migros' ? 'Migros-Pilot-Run' : 'Pilot-Run';
      const idPart = inspection.sku_number || inspection.id.slice(0, 8);

      const blob = await pdf(docElement).toBlob();
      const dlUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = dlUrl;
      a.download = `${filenamePrefix}-${idPart}.pdf`;
      a.click();
      URL.revokeObjectURL(dlUrl);
    } catch (err: any) {
      console.error('PDF generation failed:', err);
      alert(`PDF generation failed: ${err?.message || 'Unknown error'}. Check browser console for details.`);
    } finally {
      setGeneratingPDF(false);
    }
  };

  const handleDownloadImages = async () => {
    if (!inspection) return;
    const allPhotos = (inspection.photos || []).filter((p: any) => p?.image_url);
    const allCtq = (inspection.ctqImages || []).filter((c: any) => c?.image_url);

    if (allPhotos.length === 0 && allCtq.length === 0) {
      alert('No images found to download.');
      return;
    }

    setDownloadingImages(true);
    try {
      const JSZip = (await import('jszip')).default;
      const zip = new JSZip();

      const fetchBlob = async (url: string): Promise<Blob | null> => {
        let abs = url;
        if (!/^(https?:|blob:|data:)/.test(abs)) abs = `${baseUrl}${abs.startsWith('/') ? '' : '/'}${abs}`;
        const isGcs = abs.includes('storage.googleapis.com');
        const fetchUrl = isGcs ? `${baseUrl}/api/v1/proxy-image?url=${encodeURIComponent(abs)}` : abs;
        try {
          const res = await fetch(fetchUrl, { mode: 'cors' });
          if (res.ok) return await res.blob();
        } catch {}
        return null;
      };

      const getExt = (url: string, blob: Blob) => {
        const match = url.match(/\.(\w{2,5})(\?|$)/);
        if (match) return match[1].toLowerCase();
        if (blob.type.includes('jpeg') || blob.type.includes('jpg')) return 'jpg';
        if (blob.type.includes('png')) return 'png';
        if (blob.type.includes('webp')) return 'webp';
        return 'jpg';
      };

      if (allPhotos.length > 0) {
        const folder = zip.folder('Pilot_Run_Images')!;
        for (let i = 0; i < allPhotos.length; i++) {
          const p = allPhotos[i];
          const blob = await fetchBlob(p.image_url);
          if (blob) {
            const ext = getExt(p.image_url, blob);
            const label = (p.label || `Photo_${i + 1}`).replace(/[^a-zA-Z0-9_\-]/g, '_');
            folder.file(`${String(i + 1).padStart(2, '0')}_${label}.${ext}`, blob);
          }
        }
      }

      if (allCtq.length > 0) {
        const folder = zip.folder('CTQ_Images')!;
        for (let i = 0; i < allCtq.length; i++) {
          const blob = await fetchBlob(allCtq[i].image_url);
          if (blob) {
            const ext = getExt(allCtq[i].image_url, blob);
            folder.file(`${String(i + 1).padStart(2, '0')}_CTQ_Detail.${ext}`, blob);
          }
        }
      }

      const zipBlob = await zip.generateAsync({ type: 'blob' });
      const idPart = inspection.sku_number || inspection.id?.slice(0, 8) || 'report';
      const a = document.createElement('a');
      a.href = URL.createObjectURL(zipBlob);
      a.download = `Pilot-Run-Images-${idPart}.zip`;
      a.click();
      URL.revokeObjectURL(a.href);
    } catch (err: any) {
      console.error('Image download failed:', err);
      alert(`Image download failed: ${err?.message || 'Unknown error'}`);
    } finally {
      setDownloadingImages(false);
    }
  };

  const resolveUrl = (url: string | null | undefined): string | null => {
    if (!url) return null;
    if (url.startsWith('http://') || url.startsWith('https://') || url.startsWith('blob:') || url.startsWith('data:')) return url;
    const baseUrl = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3000';
    return `${baseUrl}${url}`;
  };

  const result = inspection.result as string; // PASS | FAIL | PENDING | COMPLETED
  const isPassed = result === 'PASS' || result === 'COMPLETED';
  const isFailed = result === 'FAIL';

  const statusBannerBg = isPassed
    ? 'bg-gradient-to-br from-emerald-400 to-emerald-600'
    : isFailed
    ? 'bg-gradient-to-br from-red-400 to-red-600'
    : 'bg-gradient-to-br from-amber-400 to-amber-600';

  const statusLabel = isPassed ? (result === 'COMPLETED' ? 'Pilot Run Completed' : 'Pilot Run Approved') : isFailed ? 'Pilot Run Rejected' : 'Awaiting Review';
  const statusValue = result === 'COMPLETED' ? 'COMPLETED' : result === 'PASS' ? 'PASS' : isFailed ? 'FAIL' : 'PENDING';
  const statusSubtext = isPassed ? 'Ready for bulk production' : isFailed ? 'Requires corrective actions' : 'Evaluation in progress';
  const riskLevel = isPassed ? 'LOW' : isFailed ? 'HIGH' : 'MEDIUM';
  const statusIcon = isPassed ? 'verified' : isFailed ? 'cancel' : 'hourglass_top';

  const evaluationScore = (() => {
    const val = parseFloat(inspection.evaluation);
    return isNaN(val) ? inspection.evaluation || '—' : `${val}`;
  })();

  const compositionText = (inspection.compositions || [])
    .map((c: any) => `${c.percentage}% ${c.material}`)
    .join(', ') || '—';

  // inspection_date has only date; take time from created_at
  const formattedDate = (() => {
    const d = inspection.inspection_date ? new Date(inspection.inspection_date) : null;
    const c = inspection.created_at ? new Date(inspection.created_at) : null;
    if (d && c && !isNaN(d.getTime()) && !isNaN(c.getTime())) {
      d.setHours(c.getHours(), c.getMinutes(), c.getSeconds());
    }
    return formatReportDate(d || inspection.inspection_date);
  })();

  const photos = (inspection.photos || []).map((p: any) => ({
    label: p.label || 'Photo',
    url: resolveUrl(p.image_url),
  }));

  const ctqImages = (inspection.ctqImages || []).map((c: any) => resolveUrl(c.image_url)).filter(Boolean);

  const processes = (inspection.processes || []).map((p: any) => p.name).filter(Boolean);
  const screens = inspection.screens || [];
  const lifetimes = inspection.lifetimes || [];

  const factorySignature = resolveUrl(inspection.factory_signature);
  const inspectorSignature = resolveUrl(inspection.inspector_signature);

  return (
    <div className="mx-auto w-full max-w-7xl pb-12 animate-in fade-in duration-500 print:pb-0 print:max-w-none print:w-full print:animate-none space-y-8">
      {/* Breadcrumbs */}
      <nav aria-label="Breadcrumb" className="flex mb-6 text-sm text-slate-500 print:hidden">
        <ol className="inline-flex items-center space-x-1 md:space-x-3">
          <li className="inline-flex items-center">
            <Link to="/dashboard" className="inline-flex items-center hover:text-slate-900 transition-colors">
              <span className="material-symbols-outlined text-[18px] mr-2">dashboard</span>
              Dashboard
            </Link>
          </li>
          <li>
            <div className="flex items-center">
              <span className="material-symbols-outlined text-slate-400 text-[18px]">chevron_right</span>
              <Link to="/pilot-run-reports" className="ml-1 hover:text-slate-900 md:ml-2 transition-colors">Pilot Run Reports</Link>
            </div>
          </li>
          <li aria-current="page">
            <div className="flex items-center">
              <span className="material-symbols-outlined text-slate-400 text-[18px]">chevron_right</span>
              <span className="ml-1 font-medium text-[#136bce] md:ml-2">#{inspection.sku_number || id}</span>
            </div>
          </li>
        </ol>
      </nav>

      {/* Header section */}
      <div className="flex flex-col items-start sm:flex-row sm:items-center justify-between gap-4 print:mb-12">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 tracking-tight leading-tight">Pilot Run Inspection Report</h1>
          <p className="text-sm text-slate-500 mt-1">First production run evaluation and approval</p>
        </div>
        <div className="flex gap-3 w-full sm:w-auto print:hidden">
          <button
            onClick={() => setPdfModalOpen(true)}
            disabled={generatingPDF}
            className="inline-flex items-center justify-center gap-2 px-6 py-2.5 rounded-lg shadow-md text-sm font-bold text-white bg-emerald-600 hover:bg-emerald-700 focus:outline-none focus:ring-4 focus:ring-emerald-500/30 transition-all active:scale-95 flex-1 sm:flex-none disabled:opacity-60 disabled:cursor-not-allowed"
          >
            <span className="material-symbols-outlined text-[20px]">
              {generatingPDF ? 'hourglass_top' : 'picture_as_pdf'}
            </span>
            {generatingPDF ? 'Generating PDF…' : 'Generate PDF'}
          </button>
          <button
            onClick={handleDownloadImages}
            disabled={downloadingImages}
            className="inline-flex items-center justify-center gap-2 px-6 py-2.5 rounded-lg shadow-md text-sm font-bold text-white bg-violet-600 hover:bg-violet-700 focus:outline-none focus:ring-4 focus:ring-violet-500/30 transition-all active:scale-95 flex-1 sm:flex-none disabled:opacity-60 disabled:cursor-not-allowed"
          >
            <span className="material-symbols-outlined text-[20px]">
              {downloadingImages ? 'hourglass_top' : 'photo_library'}
            </span>
            {downloadingImages ? 'Zipping…' : 'Download Images'}
          </button>
          <button
            onClick={handleDownload}
            className="inline-flex items-center justify-center px-6 py-2.5 rounded-lg shadow-md text-sm font-bold text-white bg-[#136bce] hover:bg-[#0e52a0] focus:outline-none focus:ring-4 focus:ring-blue-500/30 transition-all active:scale-95 flex-1 sm:flex-none"
          >
            <span className="material-symbols-outlined mr-2 text-[20px]">download</span>
            Download Report
          </button>
        </div>
      </div>

      {/* Status Banner */}
      <div className="bg-white rounded-2xl shadow-md border border-slate-200 overflow-hidden flex flex-col md:flex-row h-auto md:h-44">
        <div className={`md:w-1/3 text-white p-6 flex flex-col items-center justify-center text-center relative overflow-hidden ${statusBannerBg}`}>
          <span className={`material-symbols-outlined absolute -right-6 -bottom-6 text-[140px] opacity-20 rotate-12 select-none z-0`}>{statusIcon}</span>
          <div className="relative z-10 w-full flex flex-col items-center justify-center h-full">
            <div className="inline-flex items-center gap-2 bg-white/20 backdrop-blur-md px-3 py-1 rounded-full text-[10px] font-bold tracking-widest uppercase mb-2 shadow-sm ring-1 ring-white/30">
              <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse"></span>
              {statusLabel}
            </div>
            <h2 className="text-5xl font-black tracking-tighter drop-shadow-sm leading-none mb-1">{statusValue}</h2>
            <p className="text-white/80 text-xs font-medium opacity-90">{statusSubtext}</p>
          </div>
        </div>
        <div className="md:w-2/3 p-6 flex items-center justify-center bg-white relative">
          <div className="text-center group">
            <p className="text-xs uppercase tracking-wider font-bold text-slate-500 mb-1">Season</p>
            <div className="flex items-baseline justify-center gap-2">
              <p className="text-xl font-black text-slate-800 group-hover:text-emerald-500 transition-colors">{inspection.season || '—'}</p>
            </div>
          </div>
        </div>
      </div>

      <div className="space-y-8 print:space-y-12">
        {/* Summary Details */}
        <div className="rounded-2xl bg-white border border-slate-200 p-8 shadow-sm print:shadow-none print:border-slate-300">
          <h3 className="text-base font-bold text-slate-800 mb-6 pb-4 border-b border-slate-100 flex items-center gap-2">
            <span className="material-symbols-outlined text-[#136bce] text-[22px]">info</span>
            Summary Details
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-y-8 gap-x-6 print:grid-cols-2 print:gap-y-10">
            <div>
              <p className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">Basis</p>
              <p className="text-base font-semibold text-slate-900 mt-1">{inspection.basis || '—'}</p>
            </div>
            <div>
              <p className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">Ref Number</p>
              <p className="text-base font-semibold text-slate-900 mt-1">{inspection.ref_number || '—'}</p>
            </div>
            <div>
              <p className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">Buyer Name</p>
              <p className="text-base font-semibold text-slate-900 mt-1">{inspection.buyer_name || '—'}</p>
            </div>
            <div>
              <p className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">SKU Number</p>
              <p className="text-base font-semibold text-slate-900 mt-1">{inspection.sku_number || '—'}</p>
            </div>
            <div>
              <p className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">Vendor Product Code</p>
              <p className="text-base font-semibold text-slate-900 mt-1">{inspection.factory_code || '—'}</p>
            </div>
            <div>
              <p className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">Vendor Product Description</p>
              <p className="text-base font-semibold text-slate-900 mt-1">{inspection.product_desc || '—'}</p>
            </div>
            <div>
              <p className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">Total Qty</p>
              <p className="text-base font-semibold text-slate-900 mt-1">{inspection.total_qty || '—'}</p>
            </div>
            <div>
              <p className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">Season</p>
              <p className="text-base font-semibold text-slate-900 mt-1">{inspection.season || '—'}</p>
            </div>
            <div>
              <p className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">Date</p>
              <p className="text-base font-semibold text-slate-900 mt-1">{formattedDate}</p>
            </div>
            {compositionText !== '—' && (
              <div>
                <p className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">Composition</p>
                <p className="text-base font-semibold text-slate-900 mt-1">{compositionText}</p>
              </div>
            )}
          </div>
        </div>

        {/* Dimensions Section */}
        {(inspection.length || inspection.width || inspection.height || inspection.weight) && (
          <div className="rounded-2xl bg-white border border-slate-200 p-8 shadow-sm print:shadow-none print:border-slate-300">
            <h3 className="text-base font-bold text-slate-800 mb-6 pb-4 border-b border-slate-100 flex items-center gap-2">
              <span className="material-symbols-outlined text-[#136bce] text-[22px]">straighten</span>
              Dimensions
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              {inspection.length && (
                <div className="p-4 bg-slate-50 rounded-xl border border-slate-100">
                  <p className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">Length</p>
                  <p className="text-lg font-bold text-slate-900 mt-1">{inspection.length} {inspection.length_unit || ''}</p>
                </div>
              )}
              {inspection.width && (
                <div className="p-4 bg-slate-50 rounded-xl border border-slate-100">
                  <p className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">Width</p>
                  <p className="text-lg font-bold text-slate-900 mt-1">{inspection.width} {inspection.width_unit || ''}</p>
                </div>
              )}
              {inspection.height && (
                <div className="p-4 bg-slate-50 rounded-xl border border-slate-100">
                  <p className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">Height</p>
                  <p className="text-lg font-bold text-slate-900 mt-1">{inspection.height} {inspection.height_unit || ''}</p>
                </div>
              )}
              {inspection.weight && (
                <div className="p-4 bg-slate-50 rounded-xl border border-slate-100">
                  <p className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">Weight</p>
                  <p className="text-lg font-bold text-slate-900 mt-1">{inspection.weight} {inspection.weight_unit || ''}</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Production Process Section */}
        {(processes.length > 0 || screens.length > 0 || lifetimes.length > 0) && (
          <div className="rounded-2xl bg-white border border-slate-200 shadow-sm overflow-hidden print:border-slate-300">
            <div className="px-8 py-5 border-b border-slate-100 bg-slate-50/50 print:bg-slate-50">
              <h3 className="text-base font-bold text-slate-800 flex items-center gap-2">
                <span className="material-symbols-outlined text-[#136bce]">factory</span>
                Production Process
              </h3>
            </div>
            <div className="p-8 space-y-10">
              {processes.length > 0 && (
                <div className="flex flex-wrap gap-4">
                  {processes.map((process: string) => (
                    <div key={process} className="flex items-center gap-2 px-4 py-2 rounded-lg border border-slate-100 bg-slate-50/50 text-sm font-bold text-slate-700">
                      <span className="material-symbols-outlined text-[#136bce] text-[18px]">check_circle</span>
                      {process}
                    </div>
                  ))}
                </div>
              )}

              {(screens.length > 0 || lifetimes.length > 0) && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  {screens.length > 0 && (
                    <div className="space-y-6">
                      <h4 className="text-sm font-bold text-slate-800 flex items-center gap-2">
                        <span className="material-symbols-outlined text-[#136bce] text-[20px]">filter_none</span>
                        Number of Screens
                      </h4>
                      <div className="overflow-hidden rounded-xl border border-slate-100 bg-white shadow-sm">
                        <table className="w-full text-left text-xs">
                          <thead className="bg-slate-50 text-slate-500 uppercase font-bold tracking-wider">
                            <tr>
                              <th className="px-4 py-3">Color</th>
                              <th className="px-4 py-3">Number</th>
                              <th className="px-4 py-3">Unit</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-slate-50 text-slate-700 font-medium">
                            {screens.map((s: any, idx: number) => (
                              <tr key={idx}>
                                <td className="px-4 py-3">{s.color}</td>
                                <td className="px-4 py-3">{s.count}</td>
                                <td className="px-4 py-3">{s.unit}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}

                  {lifetimes.length > 0 && (
                    <div className="space-y-6">
                      <h4 className="text-sm font-bold text-slate-800 flex items-center gap-2">
                        <span className="material-symbols-outlined text-[#136bce] text-[20px]">history</span>
                        Lifetime
                      </h4>
                      <div className="overflow-hidden rounded-xl border border-slate-100 bg-white shadow-sm">
                        <table className="w-full text-left text-xs">
                          <thead className="bg-slate-50 text-slate-500 uppercase font-bold tracking-wider">
                            <tr>
                              <th className="px-4 py-3">Color</th>
                              <th className="px-4 py-3">Number</th>
                              <th className="px-4 py-3">Unit</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-slate-50 text-slate-700 font-medium">
                            {lifetimes.map((l: any, idx: number) => (
                              <tr key={idx}>
                                <td className="px-4 py-3">{l.color}</td>
                                <td className="px-4 py-3">{l.count}</td>
                                <td className="px-4 py-3">{l.unit}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Pilot Run Images */}
        {photos.length > 0 && (
          <div className="rounded-2xl bg-white border border-slate-200 shadow-sm p-8 break-inside-avoid print:border-slate-300">
            <h3 className="text-base font-bold text-slate-800 mb-6 flex items-center gap-2 border-b border-slate-100 pb-4">
              <span className="material-symbols-outlined text-[#136bce] text-[22px]">photo_library</span>
              Pilot Run Images
            </h3>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-4 mt-6">
              {photos.map((img: any, idx: number) => (
                <div key={idx} className="flex flex-col gap-2 print:break-inside-avoid">
                  <div
                    className="group relative aspect-square rounded-xl bg-slate-50 overflow-hidden border border-slate-200 cursor-zoom-in shadow-sm hover:shadow-md transition-all print:border-slate-300 print:bg-slate-100"
                    onClick={() => img.url && openPreview(img.url)}
                  >
                    {img.url ? (
                      <img
                        src={img.url}
                        alt={img.label}
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <span className="material-symbols-outlined text-slate-300 text-[32px]">image_not_supported</span>
                      </div>
                    )}
                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors"></div>
                  </div>
                  <div className="text-center">
                    <span className="text-[10px] font-bold text-slate-500 uppercase tracking-tighter print:text-slate-700">{img.label}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Evaluation Section */}
        {(inspection.evaluation || inspection.ctq_remarks || inspection.corrective_actions || ctqImages.length > 0) && (
          <div className="rounded-2xl bg-white border border-slate-200 p-8 shadow-sm print:shadow-none print:border-slate-300">
            <h3 className="text-base font-bold text-slate-800 mb-6 pb-4 border-b border-slate-100 flex items-center gap-2">
              <span className="material-symbols-outlined text-[#136bce] text-[22px]">rate_review</span>
              Evaluation
            </h3>
            <div className="space-y-8">
              {inspection.ctq_remarks && (
                <div className="p-6 bg-slate-50 rounded-xl border border-slate-100">
                  <p className="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-2">CTQ Remarks</p>
                  <p className="text-sm text-slate-700 font-medium leading-relaxed mb-4">{inspection.ctq_remarks}</p>
                  {ctqImages.length > 0 && (
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-6">
                      {ctqImages.map((imgUrl: string, idx: number) => (
                        <div key={idx} className="flex flex-col gap-2">
                          <div
                            className="group relative aspect-video rounded-lg overflow-hidden border border-slate-200 shadow-sm cursor-zoom-in"
                            onClick={() => openPreview(imgUrl)}
                          >
                            <img src={imgUrl} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110" alt={`CTQ Detail ${idx + 1}`} />
                            <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors"></div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
              {inspection.evaluation && (
                <div className="p-6 bg-blue-50 rounded-xl border border-blue-100">
                  <p className="text-[10px] font-bold text-blue-600 uppercase tracking-wider mb-2">Overall Evaluation</p>
                  <p className="text-sm text-slate-700 font-medium leading-relaxed">{inspection.evaluation}</p>
                </div>
              )}
              {inspection.corrective_actions && (
                <div className="p-6 bg-orange-50 rounded-xl border border-orange-100">
                  <p className="text-[10px] font-bold text-orange-600 uppercase tracking-wider mb-2">Corrective Actions</p>
                  <p className="text-sm text-slate-700 font-medium leading-relaxed">{inspection.corrective_actions}</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Signatures Section */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 pb-0 break-inside-avoid print:pb-0 print:gap-10">
          <div className="rounded-2xl bg-white border border-slate-200 shadow-sm p-8 flex flex-col print:border-slate-300 print:p-6">
            <h3 className="text-base font-bold text-slate-800 mb-4 flex items-center gap-2">
              <span className="material-symbols-outlined text-[#136bce]">person</span>
              Factory Rep Signature
            </h3>
            <div className="flex-1 rounded-xl border-2 border-dashed border-slate-200 bg-slate-50 flex items-center justify-center min-h-[160px] relative group overflow-hidden hover:bg-slate-100 hover:border-[#136bce]/30 transition-all print:border-slate-300 print:bg-slate-100">
              {factorySignature ? (
                <div className="relative z-10 flex flex-col items-center">
                  <img
                    alt="Factory Rep Signature"
                    className="h-20 object-contain opacity-90 transition-transform group-hover:scale-105"
                    src={factorySignature}
                  />
                </div>
              ) : (
                <p className="text-sm text-slate-400 font-medium">No signature available</p>
              )}
            </div>
          </div>
          <div className="rounded-2xl bg-white border border-slate-200 shadow-sm p-8 flex flex-col print:border-slate-300 print:p-6">
            <h3 className="text-base font-bold text-slate-800 mb-4 flex items-center gap-2">
              <span className="material-symbols-outlined text-[#136bce]">verified_user</span>
              QA Signature
            </h3>
            <div className="flex-1 rounded-xl border-2 border-dashed border-slate-200 bg-slate-50 flex items-center justify-center min-h-[160px] relative group overflow-hidden hover:bg-slate-100 hover:border-[#136bce]/30 transition-all print:border-slate-300 print:bg-slate-100">
              {inspectorSignature ? (
                <div className="relative z-10 flex flex-col items-center">
                  <img
                    alt="QA Signature"
                    className="h-20 object-contain opacity-90 transition-transform group-hover:scale-105"
                    src={inspectorSignature}
                  />
                  <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mt-2">
                    {inspection.inspector?.name || inspection.inspector_name || 'QA Inspector'}
                  </p>
                </div>
              ) : (
                <p className="text-sm text-slate-400 font-medium">No signature available</p>
              )}
            </div>
          </div>
        </div>

        {/* Source of Truth Section */}
        <div className="rounded-2xl bg-white border border-slate-200 p-8 shadow-sm print:hidden">
          <div className="flex flex-col md:flex-row md:items-center justify-between mb-6 pb-4 border-b border-slate-100 gap-4">
            <h3 className="text-base font-bold text-slate-800 flex items-center gap-2">
              <span className="material-symbols-outlined text-[#136bce] text-[22px]">verified</span>
              Source of Truth
            </h3>
            <div className="relative w-full md:w-64">
              <span className="material-symbols-outlined absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-[18px]">search</span>
              <input
                type="text"
                placeholder="Search logs..."
                className="w-full pl-10 pr-4 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 transition-all"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>

          <div className="flex flex-col gap-10">
            <div className="space-y-6 max-w-3xl">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="p-4 bg-slate-50 rounded-xl border border-slate-100">
                  <p className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">SKU Number</p>
                  <p className="text-sm font-bold text-slate-900 mt-1">{inspection.sku_number || 'N/A'}</p>
                </div>
                <div className="p-4 bg-slate-50 rounded-xl border border-slate-100">
                  <p className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">Vendor Product Code</p>
                  <p className="text-sm font-bold text-slate-900 mt-1">{inspection.factory_code || 'N/A'}</p>
                </div>
              </div>

              <div className="space-y-3">
                <label className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">Add Comment / Decision</label>
                <textarea
                  className="w-full p-4 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 transition-all resize-none min-h-[100px]"
                  placeholder="Type your comment here..."
                  value={newComment}
                  onChange={(e) => setNewComment(e.target.value)}
                ></textarea>

                <div className="flex flex-wrap gap-2">
                  <button
                    onClick={() => setNewComment('Sample Approved')}
                    className="px-3 py-1.5 bg-emerald-50 text-emerald-700 text-[10px] font-bold rounded-lg border border-emerald-100 hover:bg-emerald-100 transition-colors"
                  >
                    Sample Approved
                  </button>
                  <button
                    onClick={() => setNewComment('Fit Sample Rejected')}
                    className="px-3 py-1.5 bg-red-50 text-red-700 text-[10px] font-bold rounded-lg border border-red-100 hover:bg-red-100 transition-colors"
                  >
                    Fit Sample Rejected
                  </button>
                </div>

                {pendingAttachments.length > 0 && (
                  <div className="flex flex-col gap-1">
                    {pendingAttachments.map((att, idx) => (
                      <div key={idx} className="flex items-center gap-2 p-2 bg-blue-50 rounded-lg border border-blue-100 text-xs text-[#136bce] font-bold">
                        <span className="material-symbols-outlined text-[16px]">description</span>
                        {att.name}
                        <button onClick={() => setPendingAttachments(prev => prev.filter((_, i) => i !== idx))} className="ml-auto text-slate-400 hover:text-red-500">
                          <span className="material-symbols-outlined text-[16px]">close</span>
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                <div className="flex items-center justify-between mt-6">
                  <label className="flex items-center gap-2 cursor-pointer group">
                    <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-slate-100 text-slate-500 group-hover:bg-blue-50 group-hover:text-[#136bce] transition-all border border-slate-200 group-hover:border-blue-200">
                      <span className="material-symbols-outlined text-[20px]">{isUploadingFile ? 'hourglass_empty' : 'attach_file'}</span>
                    </div>
                    <div className="flex flex-col">
                      <span className="text-xs font-bold text-slate-700 group-hover:text-[#136bce] transition-colors">
                        {isUploadingFile ? 'Uploading...' : 'Attach File'}
                      </span>
                      <span className="text-[10px] text-slate-400 font-medium tracking-tight">PDF, JPG, PNG up to 10MB</span>
                    </div>
                    <input type="file" className="hidden" onChange={handleFileUpload} disabled={isUploadingFile} multiple />
                  </label>
                  <button
                    onClick={handleAddComment}
                    disabled={isPostingLog || !newComment.trim()}
                    className="px-8 py-2.5 bg-[#136bce] text-white text-sm font-bold rounded-xl hover:bg-[#0e52a0] transition-all shadow-md hover:shadow-lg active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isPostingLog ? 'Posting...' : 'Post Log'}
                  </button>
                </div>
              </div>
            </div>

            <div className="flex flex-col pt-6 border-t border-slate-100">
              <label className="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-4">Audit Trail & History</label>
              <div className="bg-slate-50 rounded-2xl border border-slate-100 p-6 overflow-y-auto max-h-[600px] space-y-6 custom-scrollbar">
                {filteredComments.map(comment => (
                  <div key={comment.id} className="bg-white p-4 rounded-xl shadow-sm border border-slate-100 hover:border-blue-100 transition-colors">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        {(comment.text.includes('Approved') || comment.text.includes('Rejected')) && (
                          <span className={`text-[9px] font-black uppercase px-2 py-0.5 rounded-full ${
                            comment.text.includes('Approved') ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'
                          }`}>
                            {comment.text.includes('Approved') ? 'Approved' : 'Rejected'}
                          </span>
                        )}
                        <span className="text-[10px] font-bold text-slate-900">{comment.author}</span>
                      </div>
                      <span className="text-[10px] text-slate-400 font-bold flex items-center gap-1">
                        <span className="material-symbols-outlined text-[14px]">schedule</span>
                        {comment.date}
                      </span>
                    </div>
                    <p className="text-xs text-slate-700 font-medium leading-relaxed">{comment.text}</p>
                    {comment.attachments?.length > 0 && (
                      <div className="mt-3 flex flex-wrap gap-2">
                        {comment.attachments.map((att: any, idx: number) => (
                          <button
                            key={idx}
                            onClick={(e) => {
                              e.preventDefault();
                              setSelectedAttachment({ name: att.name, url: resolveUrl(att.url) || '#' });
                            }}
                            className="flex items-center gap-2 p-2 bg-slate-50 rounded-lg border border-slate-100 text-[10px] text-[#136bce] font-bold hover:bg-blue-50 transition-colors w-fit text-left focus:outline-none"
                          >
                            <span className="material-symbols-outlined text-[16px]">description</span>
                            {att.name}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
                {filteredComments.length === 0 && (
                  <div className="h-full flex flex-col items-center justify-center text-slate-400 py-12">
                    <span className="material-symbols-outlined text-[48px] mb-3 opacity-20">history_toggle_off</span>
                    <p className="text-xs font-bold uppercase tracking-widest">No logs found</p>
                    <p className="text-[10px] font-medium mt-1">Try adjusting your search term</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Attachment Preview Modal */}
      {selectedAttachment && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 sm:p-6 print:hidden">
          <div
            className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm"
            onClick={() => setSelectedAttachment(null)}
          />
          <div className="relative w-full max-w-4xl bg-white rounded-2xl shadow-2xl overflow-hidden border border-slate-200 flex flex-col max-h-[90vh]">
            <div className="flex items-center justify-between p-4 border-b border-slate-100 shrink-0">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-blue-50 flex items-center justify-center text-[#136bce]">
                  <span className="material-symbols-outlined">description</span>
                </div>
                <div>
                  <h4 className="text-sm font-bold text-slate-900">{selectedAttachment.name}</h4>
                  <p className="text-[10px] text-slate-500 font-medium">Attachment Preview</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <a 
                  href={selectedAttachment.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-8 h-8 rounded-lg flex items-center justify-center text-slate-400 hover:bg-blue-50 hover:text-[#136bce] transition-all"
                  title="Download File"
                >
                  <span className="material-symbols-outlined text-[20px]">download</span>
                </a>
                <button
                  onClick={() => setSelectedAttachment(null)}
                  className="w-8 h-8 rounded-lg flex items-center justify-center text-slate-400 hover:bg-red-50 hover:text-red-500 transition-all"
                  title="Close"
                >
                  <span className="material-symbols-outlined text-[20px]">close</span>
                </button>
              </div>
            </div>
            <div className="p-4 bg-slate-50 flex-1 overflow-auto flex flex-col items-center justify-center min-h-[300px] text-center rounded-b-2xl">
              {/\.(jpg|jpeg|png|gif|webp|svg)$/i.test(selectedAttachment.name) ? (
                <img src={selectedAttachment.url} alt={selectedAttachment.name} className="max-w-full max-h-[70vh] object-contain rounded-lg shadow-sm border border-slate-200 bg-white" />
              ) : /\.(pdf)$/i.test(selectedAttachment.name) ? (
                <iframe src={selectedAttachment.url} title={selectedAttachment.name} className="w-full h-[70vh] rounded-lg border border-slate-200 shadow-sm bg-white"></iframe>
              ) : (
                <div className="flex flex-col items-center justify-center py-12">
                  <div className="w-20 h-20 rounded-full bg-white shadow-sm flex items-center justify-center mb-4 border border-slate-100">
                    <span className="material-symbols-outlined text-[40px] text-slate-300">draft</span>
                  </div>
                  <h5 className="text-base font-bold text-slate-800 mb-2">Detailed Preview Not Available</h5>
                  <p className="text-xs text-slate-500 max-w-xs leading-relaxed">
                    The file <span className="font-bold text-slate-700">{selectedAttachment.name}</span> cannot be previewed directly in the browser window.
                  </p>
                  <div className="mt-8 flex gap-3">
                    <a 
                      href={selectedAttachment.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="px-6 py-2 bg-[#136bce] text-white text-xs font-bold rounded-xl hover:bg-[#0e52a0] transition-all shadow-md active:scale-95 flex items-center gap-2"
                    >
                      <span className="material-symbols-outlined text-[18px]">download</span>
                      Download File
                    </a>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      <div className="flex items-center justify-end text-[11px] text-slate-400 font-bold px-2 tracking-wide uppercase print:mt-20 print:border-t print:pt-6 print:border-slate-200">
        <span className="hidden print:block text-slate-300">System-Generated Pilot Run Document</span>
      </div>

      {/* Image Preview Modal */}
      {previewImage && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-900/90 backdrop-blur-sm p-4 animate-in fade-in duration-200 print:hidden"
          onClick={() => { setPreviewImage(null); setZoom(1); }}
        >
          {/* Close */}
          <button
            className="absolute top-6 right-6 text-white hover:text-slate-300 transition-colors p-2 rounded-full bg-slate-800/50 z-10"
            onClick={(e) => { e.stopPropagation(); setPreviewImage(null); setZoom(1); }}
          >
            <span className="material-symbols-outlined text-[32px]">close</span>
          </button>

          {/* Zoom controls */}
          <div
            className="absolute bottom-6 left-1/2 -translate-x-1/2 flex items-center gap-3 bg-slate-900/80 backdrop-blur-md border border-white/10 rounded-full px-4 py-2 z-10"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              className="text-white/70 hover:text-white transition-colors disabled:opacity-30"
              onClick={() => setZoom((prev: number) => Math.max(0.25, parseFloat((prev - 0.25).toFixed(2))))}
              disabled={zoom <= 0.25}
            >
              <span className="material-symbols-outlined text-[22px]">remove</span>
            </button>
            <span className="text-white text-xs font-bold tracking-widest w-12 text-center select-none">
              {Math.round(zoom * 100)}%
            </span>
            <button
              className="text-white/70 hover:text-white transition-colors disabled:opacity-30"
              onClick={() => setZoom((prev: number) => Math.min(5, parseFloat((prev + 0.25).toFixed(2))))}
              disabled={zoom >= 5}
            >
              <span className="material-symbols-outlined text-[22px]">add</span>
            </button>
            <div className="w-px h-4 bg-white/20 mx-1" />
            <button
              className="text-white/70 hover:text-white transition-colors"
              onClick={() => setZoom(1)}
            >
              <span className="material-symbols-outlined text-[20px]">fit_screen</span>
            </button>
          </div>

          {/* Image container — wheel listener attached via ref */}
          <div
            ref={zoomContainerRef}
            className="flex items-center justify-center overflow-hidden max-h-[90vh] max-w-[90vw]"
            style={{ cursor: zoom > 1 ? 'grab' : 'zoom-in' }}
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={previewImage}
              alt="Preview"
              className="rounded-lg shadow-2xl animate-in zoom-in-95 duration-300 max-h-[82vh] max-w-[88vw] object-contain"
              style={{ transform: `scale(${zoom})`, transformOrigin: 'center', transition: 'transform 0.1s ease' }}
              draggable={false}
            />
          </div>
        </div>
      )}
      {/* PDF Generation Modal — premium animated */}
      {generatingPDF && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center bg-slate-900/70 backdrop-blur-md p-4 animate-in fade-in duration-300 print:hidden">
          <div className="relative w-full max-w-md overflow-hidden rounded-2xl bg-white shadow-2xl ring-1 ring-slate-200 animate-in zoom-in-95 slide-in-from-bottom-4 duration-500">
            <div className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-emerald-400 via-blue-500 to-emerald-400 bg-[length:200%_100%] animate-gradient-x" />
            <div className="pointer-events-none absolute -top-16 -right-16 h-48 w-48 rounded-full bg-emerald-300/30 blur-3xl" />
            <div className="pointer-events-none absolute -bottom-16 -left-16 h-48 w-48 rounded-full bg-blue-300/30 blur-3xl" />
            <div className="relative p-8 flex flex-col items-center text-center">
              <div className="relative mb-6">
                <div className="absolute inset-0 rounded-full bg-gradient-to-br from-emerald-400 to-blue-500 blur-xl opacity-50 animate-pulse" />
                <div className="relative h-20 w-20 rounded-2xl bg-gradient-to-br from-emerald-500 to-blue-600 flex items-center justify-center shadow-lg shadow-emerald-500/40">
                  <span className="material-symbols-outlined text-white text-[40px] animate-pulse">picture_as_pdf</span>
                </div>
                <div className="absolute inset-0 animate-spin" style={{ animationDuration: '3s' }}>
                  <div className="absolute -top-1 left-1/2 -translate-x-1/2 h-3 w-3 rounded-full bg-emerald-500 shadow-md shadow-emerald-500/50" />
                </div>
              </div>
              <h2 className="text-2xl font-black text-slate-900 tracking-tight mb-2">Generating your PDF</h2>
              <p className="text-sm text-slate-500 font-medium leading-relaxed mb-6 max-w-xs">
                We're crafting a beautifully formatted pilot run report. This usually takes just a few seconds.
              </p>
              <div className="w-full bg-slate-50 rounded-xl border border-slate-100 px-4 py-3 mb-6 overflow-hidden">
                <div className="flex items-center gap-3">
                  <div className="relative flex h-2 w-2 shrink-0">
                    <span className="absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75 animate-ping" />
                    <span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-500" />
                  </div>
                  <p key={pdfStatusIndex} className="text-sm font-bold text-slate-700 text-left flex-1 animate-in fade-in slide-in-from-bottom-1 duration-500">
                    {pdfStatusMessages[pdfStatusIndex]}
                  </p>
                </div>
              </div>
              <div className="w-full h-1.5 bg-slate-100 rounded-full overflow-hidden">
                <div className="h-full w-1/3 bg-gradient-to-r from-emerald-500 to-blue-500 rounded-full animate-pdf-progress" />
              </div>
              <p className="mt-6 text-[10px] uppercase tracking-widest font-bold text-slate-400">
                Powered by <span className="text-emerald-600">QA Mate</span>
              </p>
            </div>
          </div>
        </div>
      )}

      {/* ───── Download Images Modal — premium animated ───── */}
      {downloadingImages && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center bg-slate-900/70 backdrop-blur-md p-4 animate-in fade-in duration-300 print:hidden">
          <div className="relative w-full max-w-md overflow-hidden rounded-2xl bg-white shadow-2xl ring-1 ring-slate-200 animate-in zoom-in-95 slide-in-from-bottom-4 duration-500">
            <div className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-violet-400 via-fuchsia-500 to-violet-400 bg-[length:200%_100%] animate-gradient-x" />
            <div className="pointer-events-none absolute -top-16 -right-16 h-48 w-48 rounded-full bg-violet-300/30 blur-3xl" />
            <div className="pointer-events-none absolute -bottom-16 -left-16 h-48 w-48 rounded-full bg-fuchsia-300/30 blur-3xl" />
            <div className="relative p-8 flex flex-col items-center text-center">
              <div className="relative mb-6">
                <div className="absolute inset-0 rounded-full bg-gradient-to-br from-violet-400 to-fuchsia-500 blur-xl opacity-50 animate-pulse" />
                <div className="relative h-20 w-20 rounded-2xl bg-gradient-to-br from-violet-500 to-fuchsia-600 flex items-center justify-center shadow-lg shadow-violet-500/40">
                  <span className="material-symbols-outlined text-white text-[40px] animate-pulse">photo_library</span>
                </div>
                <div className="absolute inset-0 animate-spin" style={{ animationDuration: '3s' }}>
                  <div className="absolute -top-1 left-1/2 -translate-x-1/2 h-3 w-3 rounded-full bg-violet-500 shadow-md shadow-violet-500/50" />
                </div>
              </div>
              <h2 className="text-2xl font-black text-slate-900 tracking-tight mb-2">Downloading Images</h2>
              <p className="text-sm text-slate-500 font-medium leading-relaxed mb-6 max-w-xs">
                Fetching and packaging all report images into a neatly organised ZIP folder.
              </p>
              <div className="w-full bg-slate-50 rounded-xl border border-slate-100 px-4 py-3 mb-6 overflow-hidden">
                <div className="flex items-center gap-3">
                  <div className="relative flex h-2 w-2 shrink-0">
                    <span className="absolute inline-flex h-full w-full rounded-full bg-violet-400 opacity-75 animate-ping" />
                    <span className="relative inline-flex h-2 w-2 rounded-full bg-violet-500" />
                  </div>
                  <p key={imageStatusIndex} className="text-sm font-bold text-slate-700 text-left flex-1 animate-in fade-in slide-in-from-bottom-1 duration-500">
                    {imageStatusMessages[imageStatusIndex]}
                  </p>
                </div>
              </div>
              <div className="w-full h-1.5 bg-slate-100 rounded-full overflow-hidden">
                <div className="h-full w-1/3 bg-gradient-to-r from-violet-500 to-fuchsia-500 rounded-full animate-pdf-progress" />
              </div>
              <p className="mt-6 text-[10px] uppercase tracking-widest font-bold text-slate-400">
                Powered by <span className="text-violet-600">QA Mate</span>
              </p>
            </div>
          </div>
        </div>
      )}

      {/* ───── PDF Template Selector Modal ───── */}
      {pdfModalOpen && (
        <div
          className="fixed inset-0 z-50 bg-slate-900/70 backdrop-blur-sm flex items-center justify-center p-4 print:hidden"
          onClick={() => !generatingPDF && setPdfModalOpen(false)}
        >
          <div
            className="bg-white rounded-2xl shadow-2xl border border-slate-200 w-full max-w-md overflow-hidden animate-in zoom-in-95 fade-in duration-200"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span className="material-symbols-outlined text-emerald-600 text-[22px]">picture_as_pdf</span>
                <h3 className="text-base font-bold text-slate-900">Generate PDF Report</h3>
              </div>
              <button
                onClick={() => setPdfModalOpen(false)}
                disabled={generatingPDF}
                className="text-slate-400 hover:text-slate-600 disabled:opacity-30"
              >
                <span className="material-symbols-outlined text-[22px]">close</span>
              </button>
            </div>

            <div className="px-6 py-5 space-y-4">
              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-slate-500 mb-2">Template</label>
                <div className="relative" ref={templateDropdownRef}>
                  <button
                    type="button"
                    onClick={() => setIsTemplateDropdownOpen(prev => !prev)}
                    disabled={generatingPDF}
                    className="w-full flex items-center gap-2 rounded-lg border border-slate-200 bg-white py-2.5 pl-3 pr-9 text-sm font-medium text-slate-700 hover:bg-slate-50 transition-colors outline-none cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    <span className={`h-6 w-6 rounded-full ${selectedTemplateOption.iconBg} flex items-center justify-center shrink-0`}>
                      <span className={`material-symbols-outlined text-[14px] ${selectedTemplateOption.iconColor}`}>
                        {selectedTemplateOption.icon}
                      </span>
                    </span>
                    <span className="truncate">{selectedTemplateOption.name}</span>
                    <span className="material-symbols-outlined pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-[18px] text-slate-400">expand_more</span>
                  </button>
                  {isTemplateDropdownOpen && (
                    <div className="absolute left-0 right-0 top-full mt-1 z-20 rounded-xl border border-slate-200 bg-white shadow-lg py-1 overflow-hidden">
                      {visibleTemplateOptions.map(opt => (
                        <button
                          key={opt.value}
                          type="button"
                          onClick={() => { setSelectedTemplate(opt.value); setIsTemplateDropdownOpen(false); }}
                          className={`w-full flex items-center gap-2.5 px-3 py-2 text-sm hover:bg-blue-50 transition-colors text-left ${selectedTemplate === opt.value ? 'bg-blue-50 text-[#136bce] font-semibold' : 'text-slate-700'}`}
                        >
                          <span className={`h-6 w-6 rounded-full ${opt.iconBg} flex items-center justify-center shrink-0`}>
                            <span className={`material-symbols-outlined text-[14px] ${opt.iconColor}`}>{opt.icon}</span>
                          </span>
                          {opt.name}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                <p className="text-[11px] text-slate-500 mt-2 leading-snug">{selectedTemplateOption.description}</p>
              </div>

              {generatingPDF && (
                <div className="rounded-lg bg-emerald-50 border border-emerald-200 px-3 py-2 flex items-center gap-2">
                  <span className="material-symbols-outlined text-emerald-600 text-[18px] animate-spin">progress_activity</span>
                  <span className="text-xs font-medium text-emerald-800">{pdfStatusMessages[pdfStatusIndex]}</span>
                </div>
              )}
            </div>

            <div className="px-6 py-4 border-t border-slate-100 bg-slate-50 flex items-center justify-end gap-2">
              <button
                onClick={() => setPdfModalOpen(false)}
                disabled={generatingPDF}
                className="rounded-lg px-4 py-2 text-sm font-bold text-slate-700 hover:bg-slate-200 transition-colors disabled:opacity-40"
              >
                Cancel
              </button>
              <button
                onClick={async () => {
                  await handleGeneratePDF(selectedTemplate);
                  setPdfModalOpen(false);
                }}
                disabled={generatingPDF}
                className="flex items-center gap-2 rounded-lg bg-emerald-600 px-5 py-2 text-sm font-bold text-white shadow-md shadow-emerald-500/30 hover:bg-emerald-700 active:scale-95 disabled:opacity-60 disabled:cursor-not-allowed"
              >
                <span className="material-symbols-outlined text-[18px]">
                  {generatingPDF ? 'hourglass_top' : 'picture_as_pdf'}
                </span>
                {generatingPDF ? 'Generating…' : 'Generate'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PilotRunReportDetail;
